import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  Skeleton,
  IconButton,
  Modal,
  Fade,
  Backdrop,
  Button,
  useTheme,
} from "@mui/material";
import {
  Event as EventIcon,
  Close as CloseIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  CameraAlt as CameraIcon,
  AccessTime as TimeIcon,
  Whatshot as FireIcon,
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import { backend_url } from "../App";
import LatestEvents from "./LatestEvents";

const MotionListItem = motion(Box);

function EventList({ eventClick }) {
  const theme = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [eventImages, setEventImages] = useState([]);
  const [imagesLoading, setImagesLoading] = useState(false);

  const config = useMemo(
    () => ({
      headers: {
        Authorization: `Token ${localStorage.getItem("token")}`,
      },
    }),
    [],
  );

  useEffect(() => {
    fetch(`${backend_url}/core/api/event/`, config)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error: ${response.status}`);
        }
        return response.json();
      })
      .then((actualData) => {
        setData(actualData.results);
        setError(null);
      })
      .catch((err) => {
        console.log(err);
        setError(err.message);
        setData(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [config]);

  const fetchEventImages = async (eventUuid) => {
    setImagesLoading(true);
    try {
      const response = await fetch(
        `${backend_url}/core/api/image/?event=${eventUuid}&page_size=100`,
        config,
      );
      const data = await response.json();
      const images = (data.results || []).map((img) => {
        let imageUrl = img.file;
        if (imageUrl.includes("http://127.0.0.1:8000")) {
          imageUrl = imageUrl.replace(
            "http://127.0.0.1:8000",
            "https://api.forestwatch.org.pk",
          );
        }
        return imageUrl;
      });
      setEventImages(images);
      setCurrentImageIndex(0);
    } catch (error) {
      console.error("Error fetching event images:", error);
    } finally {
      setImagesLoading(false);
    }
  };

  const handleEventClick = (event) => {
    if (eventClick) {
      eventClick(event);
    }
    setSelectedEvent(event);
    fetchEventImages(event.uuid);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedEvent(null);
    setEventImages([]);
    setCurrentImageIndex(0);
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % eventImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex(
      (prev) => (prev - 1 + eventImages.length) % eventImages.length,
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Unknown";
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Stack spacing={2}>
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton
              key={i}
              variant="rounded"
              height={100}
              sx={{ borderRadius: 2 }}
            />
          ))}
        </Stack>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          Error loading events: {error}
        </Alert>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <Box
          sx={{
            p: 2,
            background:
              "linear-gradient(135deg, rgba(30,30,40,0.95) 0%, rgba(20,20,30,0.98) 100%)",
            borderBottom: "1px solid rgba(255,255,255,0.1)",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: "#fff",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <EventIcon sx={{ color: "#ffd93d" }} />
            Latest Events
            {data && data.length > 0 && (
              <Chip
                label={data.length}
                size="small"
                sx={{
                  bgcolor: "rgba(255,255,255,0.2)",
                  color: "#fff",
                  fontWeight: 600,
                }}
              />
            )}
          </Typography>
        </Box>

        {/* Events List */}
        <Box sx={{ flex: 1, overflowY: "auto", p: 1 }}>
          <AnimatePresence>
            {data && data.length > 0 ? (
              <Stack spacing={1.5}>
                {data.map((event, index) => (
                  <MotionListItem
                    key={event.uuid}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    whileHover={{ scale: 1.01, x: 4 }}
                    whileTap={{ scale: 0.99 }}
                    sx={{ cursor: "pointer" }}
                    onClick={() => handleEventClick(event)}
                  >
                    <LatestEvents data={event} />
                  </MotionListItem>
                ))}
              </Stack>
            ) : (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <EventIcon
                  sx={{ fontSize: 48, color: "rgba(255,255,255,0.3)", mb: 2 }}
                />
                <Typography
                  variant="body2"
                  sx={{ color: "rgba(255,255,255,0.5)" }}
                >
                  No events found
                </Typography>
              </Box>
            )}
          </AnimatePresence>
        </Box>
      </Box>

      {/* Fullscreen Modal for Event Media */}
      <Modal
        open={modalOpen}
        onClose={handleCloseModal}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 500,
            sx: {
              backgroundColor: "rgba(0,0,0,0.9)",
              backdropFilter: "blur(10px)",
            },
          },
        }}
      >
        <Fade in={modalOpen}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "90%",
              maxWidth: 1200,
              maxHeight: "90vh",
              bgcolor: "rgba(20,20,30,0.95)",
              backdropFilter: "blur(20px)",
              borderRadius: 4,
              boxShadow: "0 25px 50px rgba(0,0,0,0.5)",
              overflow: "hidden",
              border: "1px solid rgba(255,255,255,0.2)",
            }}
          >
            {/* Modal Header */}
            <Box
              sx={{
                p: 2,
                borderBottom: "1px solid rgba(255,255,255,0.1)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                background:
                  "linear-gradient(135deg, rgba(30,30,40,0.95) 0%, rgba(20,20,30,0.98) 100%)",
              }}
            >
              <Box>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, color: "#fff" }}
                >
                  {selectedEvent?.camera_name || `Event Details`}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: "rgba(255,255,255,0.5)" }}
                >
                  {formatDate(selectedEvent?.date)}
                </Typography>
              </Box>
              <IconButton onClick={handleCloseModal} sx={{ color: "#fff" }}>
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Modal Content */}
            <Box
              sx={{
                p: 3,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              {imagesLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: 400,
                  }}
                >
                  <CircularProgress sx={{ color: "#ff6b6b" }} />
                </Box>
              ) : eventImages.length > 0 ? (
                <>
                  <motion.div
                    key={currentImageIndex}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3 }}
                    style={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <img
                      src={eventImages[currentImageIndex]}
                      alt={`Event frame ${currentImageIndex + 1}`}
                      style={{
                        maxWidth: "100%",
                        maxHeight: "60vh",
                        objectFit: "contain",
                        borderRadius: 16,
                        boxShadow: "0 10px 30px rgba(0,0,0,0.3)",
                      }}
                    />
                  </motion.div>

                  {eventImages.length > 1 && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        mt: 3,
                      }}
                    >
                      <IconButton
                        onClick={prevImage}
                        sx={{
                          bgcolor: "rgba(255,255,255,0.1)",
                          "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
                        }}
                      >
                        <ChevronLeftIcon sx={{ color: "#fff" }} />
                      </IconButton>
                      <Typography
                        variant="caption"
                        sx={{ color: "rgba(255,255,255,0.7)" }}
                      >
                        {currentImageIndex + 1} / {eventImages.length}
                      </Typography>
                      <IconButton
                        onClick={nextImage}
                        sx={{
                          bgcolor: "rgba(255,255,255,0.1)",
                          "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
                        }}
                      >
                        <ChevronRightIcon sx={{ color: "#fff" }} />
                      </IconButton>
                    </Box>
                  )}

                  {/* Thumbnail Strip */}
                  {eventImages.length > 1 && (
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        mt: 3,
                        overflowX: "auto",
                        maxWidth: "100%",
                        pb: 1,
                        "&::-webkit-scrollbar": { height: 6 },
                        "&::-webkit-scrollbar-track": {
                          bgcolor: "rgba(255,255,255,0.1)",
                          borderRadius: 3,
                        },
                        "&::-webkit-scrollbar-thumb": {
                          bgcolor: "rgba(255,255,255,0.3)",
                          borderRadius: 3,
                        },
                      }}
                    >
                      {eventImages.map((img, idx) => (
                        <motion.div
                          key={idx}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <Box
                            onClick={() => setCurrentImageIndex(idx)}
                            sx={{
                              width: 60,
                              height: 60,
                              borderRadius: 2,
                              overflow: "hidden",
                              cursor: "pointer",
                              border:
                                idx === currentImageIndex
                                  ? `2px solid ${theme.palette.primary.main}`
                                  : "2px solid transparent",
                              transition: "all 0.2s ease",
                              "&:hover": { opacity: 0.8 },
                            }}
                          >
                            <img
                              src={img}
                              alt={`Thumbnail ${idx + 1}`}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          </Box>
                        </motion.div>
                      ))}
                    </Box>
                  )}
                </>
              ) : (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <Typography
                    variant="body1"
                    sx={{ color: "rgba(255,255,255,0.7)" }}
                  >
                    No images available for this event
                  </Typography>
                  {selectedEvent?.file && (
                    <Button
                      component="a"
                      href={selectedEvent.file}
                      target="_blank"
                      variant="contained"
                      sx={{ mt: 2 }}
                    >
                      View GIF
                    </Button>
                  )}
                </Box>
              )}
            </Box>

            {/* Event Info Footer */}
            {selectedEvent && (
              <Box
                sx={{
                  p: 2,
                  borderTop: "1px solid rgba(255,255,255,0.1)",
                  display: "flex",
                  gap: 2,
                  flexWrap: "wrap",
                  bgcolor: "rgba(0,0,0,0.3)",
                }}
              >
                <Chip
                  icon={<CameraIcon />}
                  label={`Camera: ${selectedEvent.camera_name || selectedEvent.camera}`}
                  size="small"
                  sx={{ bgcolor: "rgba(255,255,255,0.1)", color: "#fff" }}
                />
                <Chip
                  icon={<TimeIcon />}
                  label={`Detected: ${formatDate(selectedEvent.date)}`}
                  size="small"
                  sx={{ bgcolor: "rgba(255,255,255,0.1)", color: "#fff" }}
                />
                {selectedEvent.species && selectedEvent.species.length > 0 && (
                  <Chip
                    icon={<FireIcon />}
                    label={`Species: ${selectedEvent.species.map((s) => s.name || s.id).join(", ")}`}
                    size="small"
                    sx={{ bgcolor: "rgba(255,255,255,0.1)", color: "#fff" }}
                  />
                )}
              </Box>
            )}
          </Box>
        </Fade>
      </Modal>
    </>
  );
}

export default EventList;
